import * as ActionTypes from '../constants/ActionTypes';
import { SAVE_LEVEL } from '../constants/Level';
import ThirdpartyService, { clearSbemGlobalConfigCache } from '../services/ThirdpartyService';
import { get, set, cloneDeep, isEqual } from 'lodash';
import { isActive } from '../components/pages/manage/apps/utils';
import { httpClient } from '../../lib/utils';
import { envValue } from '../../config/envUtils';
import { getNonce } from '../utils/get-nonce';

const transformSavedDomainData = (thirdPartyData, selectedLevel) => {
    const cspConfiguration = selectedLevel === SAVE_LEVEL.HOTEL ? 'hotelCspConfiguration' : 'chainCspConfiguration';
    const sojernTracking = _.get(thirdPartyData, 'sojernTracking');
    const trivago = _.get(thirdPartyData, 'trivago');
    const tripadvisor = _.get(thirdPartyData, 'tripadvisor');
    const thehotelnetwork = _.get(thirdPartyData, 'thehotelnetwork');
    const googleAds = _.get(thirdPartyData, 'googleAds');
    const bingAds = _.get(thirdPartyData, 'bingAds');
    const uplift = _.get(thirdPartyData, 'uplift');
    // these three keys can be set in postman only.  this code prevents them from being erased when saving other changes
    const makeCspReportOnly = _.get(thirdPartyData, 'makeCspReportOnly');
    const removeStrictDynamicFromCsp = _.get(thirdPartyData, 'removeStrictDynamicFromCsp');
    const inContextBeta = _.get(thirdPartyData, 'inContextBeta');

    return {
        [cspConfiguration]: {
            listOfDefaultDomains: _.get(thirdPartyData, `${cspConfiguration}.listOfDefaultDomains`, []),
            listOfInContextHostDomains: _.get(thirdPartyData, `${cspConfiguration}.listOfInContextHostDomains`, [])
        },
        sojernTracking,
        googleAds,
        bingAds,
        thehotelnetwork,
        trivago,
        tripadvisor,
        uplift,
        makeCspReportOnly,
        removeStrictDynamicFromCsp,
        inContextBeta
    };
};

const transformSavedAppsData = (thirdPartySavedData, savedApps) => {
    const transformedData = {
        ...thirdPartySavedData,
        ...savedApps
    };

    _.unset(transformedData, 'trivago.advertiserIdIsInvalid');

    return transformedData;
};

const transformSavedDataForRequest = (thirdPartySavedData, selectedLevel, comment = '', name = '') => ({
    Data: transformSavedDomainData(thirdPartySavedData, selectedLevel),
    MetaData: {
        Comment: comment,
        Name: name
    }
});

export const getAllThirdpartyConfigurations = (chainId, hotelId, selectedLevel) => {
    return dispatch => {
        dispatch({ type: ActionTypes.START_THIRDPARTY_CONFIGURATION_FETCH });

        return ThirdpartyService.fetchAllThirdPartyConfigurations(chainId, hotelId, selectedLevel)
            .then(response => dispatch({ type: ActionTypes.GET_ALL_THIRDPARTY_CONFIGURATIONS, data: response.Value }))
            .catch(error => dispatch({ type: ActionTypes.ERROR_THIRDPARTY_CONFIGURATION_FETCH, error }));
    };
};

export const getThirdpartyConfiguration = (chainId, hotelId, selectedLevel) => {
    return dispatch => {
        dispatch({ type: ActionTypes.START_THIRDPARTY_CONFIGURATION_FETCH });

        return ThirdpartyService.fetchThirdPartyConfig(chainId, hotelId, selectedLevel)
            .then(response => {
                dispatch({
                    type: ActionTypes.GET_THIRDPARTY_CONFIGURATION,
                    data: get(response, 'Value.Explicit.Data', {}),
                    parentData: selectedLevel === SAVE_LEVEL.CHAIN ? null : get(response, 'Value.Folded', {}),
                    level: selectedLevel
                });
            })
            .catch(error => {
                dispatch({ type: ActionTypes.ERROR_THIRDPARTY_CONFIGURATION_FETCH, error });
            });
    };
};

export const updateThirdpartyDomains = (chainId, hotelId, selectedLevel) => {
    return (dispatch, getState) => {
        dispatch({ type: ActionTypes.START_THIRDPARTY_CONFIGURATION_FETCH });
        const state = getState();
        const savedDataForRequest = transformSavedDataForRequest(state.app.data.thirdparty.data, selectedLevel);

        return ThirdpartyService.saveThirdPartyConfig(chainId, hotelId, selectedLevel, savedDataForRequest)
            .then(() => {
                dispatch(setChangedManageApps(false));

                return dispatch(getThirdpartyConfiguration(chainId, hotelId, selectedLevel));
            })
            .catch(error => dispatch({ type: ActionTypes.ERROR_THIRDPARTY_CONFIGURATION_FETCH, error }));
    };
};

export const saveLocalChanges = (data, domainListKey = 'listOfDefaultDomains') => {
    return (dispatch, getState) => {
        const state = getState();
        const { level: selectedLevel } = state.app.selected;
        const cspConfiguration = selectedLevel === SAVE_LEVEL.CHAIN ? 'chainCspConfiguration' : 'hotelCspConfiguration';
        const currentCspConfig = get(state, `app.data.thirdparty.data.${cspConfiguration}`, {});

        dispatch({
            type: ActionTypes.SET_THIRDPARTY_DOMAINS
        });
        dispatch({
            type: ActionTypes.SET_LOCAL_CHANGES,
            data: {
                ...state.app.data.thirdparty.data,
                [cspConfiguration]: {
                    ...currentCspConfig,
                    [domainListKey]: data
                }
            }
        });
        dispatch(setChangedManageApps(true));
    };
};

function sendSojernConsent(state) {
    const sojernTrackingStatus = get(state, 'app.data.thirdparty.data.sojernTracking.status');
    const parentSojernTrackingStatus = get(state, 'app.data.thirdparty.parentData.sojernTracking.status');
    const status = sojernTrackingStatus === 'inherit' ? parentSojernTrackingStatus : sojernTrackingStatus;
    const pcParam = status === 'active' ? 'ON' : 'OFF';

    let params;
    if (get(state, 'app.selected.level') === 'chain') {
        const chainId = get(state, 'app.selected.chain.Id');
        const chainName = get(
            get(state, 'app.selected.selectedHotel.chainList.chain', []).find(c => c.id === chainId),
            'name'
        );
        params = {
            pn: chainId,
            hb: chainName,
            pt: 'SEARCH',
            pc: pcParam
        };
    } else {
        params = {
            hpid: get(state, 'app.selected.selectedHotel.id'),
            hpr: get(state, 'app.selected.selectedHotel.name'),
            pt: 'SEARCH',
            pc: pcParam
        };
    }

    params.et =
        { HOME_PAGE: null, SEARCH: 'hs', PRODUCT: 'hpr', SHOPPING_CART: 'hcart', CONVERSION: 'hc', 'TRACKING ': null }[
            params.pt
        ] || '';
    const paramsQueryString = Object.keys(params)
        .map(k => `${k}=${encodeURI(params[k])}`)
        .join('&');
    const pl = document.createElement('script');
    pl.type = 'text/javascript';
    pl.async = true;
    pl.nonce = getNonce();
    pl.src = `https://beacon.sojern.com/pixel/cp/11?f_v=cp_v3_js&p_v=2&${paramsQueryString}`;
    (document.getElementsByTagName('head')[0] || document.getElementsByTagName('body')[0]).appendChild(pl);
}

function sendTHNConsent(state) {
    const thnStatus = get(state, 'app.data.thirdparty.data.thehotelnetwork.status');
    const parentThnStatusIsActive =
        get(state, 'app.data.thirdparty.parentData.thehotelnetwork.status', '') === 'active';
    const shouldSendActivationRequest = thnStatus === 'active' || (thnStatus === 'inherit' && parentThnStatusIsActive);

    if (shouldSendActivationRequest) {
        const isHotelLevel = get(state, 'app.selected.level') === 'hotel';
        const ngbeUrl = envValue('NGBE');

        httpClient.post(`${ngbeUrl}/consent`, {
            requestType: 'thnConsent',
            chainId: get(state, 'app.selected.chain.Id'),
            hotelId: isHotelLevel ? get(state, 'app.selected.selectedHotel.id') : undefined
        });
    }
}

export const saveThirdpartyApps = (chainId, hotelId, selectedLevel, savedObject, comment) => {
    return (dispatch, getState) => {
        dispatch({ type: ActionTypes.START_THIRDPARTY_CONFIGURATION_FETCH });
        const state = getState();
        const savedData = transformSavedAppsData(state.app.data.thirdparty.data, savedObject);
        const savedDataForRequest = transformSavedDataForRequest(savedData, selectedLevel, comment);
        const inContextBeta = get(state, 'app.selected.inContextBeta', false);

        return ThirdpartyService.saveThirdPartyConfig(chainId, hotelId, selectedLevel, savedDataForRequest)
            .then(() => {
                const isSojernChanged = get(state, 'app.data.thirdparty.isSojernChanged');
                const isTHNChanged = get(state, 'app.data.thirdparty.isTHNChanged');

                if (inContextBeta) {
                    clearSbemGlobalConfigCache(chainId, hotelId, selectedLevel);
                }

                dispatch(setChangedManageApps(false));
                dispatch({ type: ActionTypes.FINISH_THIRDPARTY_CONFIGURATION_FETCH });
                if (isSojernChanged) {
                    sendSojernConsent(state);
                    dispatch({ type: ActionTypes.SET_SOJERN_STATUS_CHANGED, isSojernChanged: false });
                }

                if (isTHNChanged) {
                    sendTHNConsent(state);
                    dispatch({ type: ActionTypes.SET_THN_STATUS_CHANGED, isTHNChanged: false });
                }

                return true;
            })
            .catch(error => {
                dispatch({ type: ActionTypes.ERROR_THIRDPARTY_CONFIGURATION_FETCH, error });

                throw error;
            });
    };
};

export const setChangedManageApps = value => {
    return dispatch => {
        dispatch({ type: ActionTypes.SET_THIRDPARTY_APPS_DATA_IS_CHANGED, changedManageApps: value });
    };
};

export const setAppsParameters = (code, value, initialSelection) => {
    return (dispatch, getState) => {
        const prevState = getState();
        const state = cloneDeep(get(prevState, 'app.data.thirdparty'));
        set(state, `data.${code}`, value);

        const isTrivagoEnabled = isActive(
            get(state, 'level'),
            get(state, 'data.trivago.status'),
            !get(state, 'parentData.trivago.status')
        );
        const isAdvertiserIdInvalid = isTrivagoEnabled && !get(state, 'data.trivago.advertiserId');

        set(state, 'data.trivago.advertiserIdIsInvalid', isAdvertiserIdInvalid);

        dispatch({ type: ActionTypes.SET_THIRDPARTY_APPS_PARAMETERS, state });

        if (code === 'sojernTracking.status') {
            const isSojernChanged = !isEqual(get(state, 'data.sojernTracking.status', ''), initialSelection);
            dispatch({ type: ActionTypes.SET_SOJERN_STATUS_CHANGED, isSojernChanged });
        }

        if (code === 'thehotelnetwork.status') {
            const isTHNChanged = !isEqual(get(state, 'data.thehotelnetwork.status', ''), initialSelection);
            dispatch({ type: ActionTypes.SET_THN_STATUS_CHANGED, isTHNChanged });
        }

        if (!get(state, 'changedManageApps')) {
            dispatch(setChangedManageApps(true));
        }
    };
};

export const reloadManageApps = (selectedLevel, chainId = false, hotelId = false) => {
    return (dispatch, getState) => {
        const prevState = getState();
        dispatch(setChangedManageApps(false));

        return dispatch(
            getThirdpartyConfiguration(
                chainId || get(prevState, 'app.selected.chain.Id', ''),
                hotelId || get(prevState, 'app.selected.selectedHotel.id', ''),
                selectedLevel
            )
        );
    };
};



// WEBPACK FOOTER //
// ./src/actions/thirdparty.js